<template>
    <Spinner v-if="displaySpinner" />
    <div class="title-and-controls">
        <EditableTitle v-model="product" icon="pi pi-box" />
        <div class="controls">
            <Button
                v-if="description && !description.content && isEditing"
                v-tooltip.bottom="{
                    value: 'Will generate a first draft of the description based on the context.',
                    showDelay: 100,
                }"
                severity="secondary"
                icon="pi pi-sparkles"
                label="Generate first draft"
                @click="generateProductDescription"
            />
        </div>
    </div>
    <section :class="['description', { 'edit': isEditing }]">
        <TextAppArtefact
            v-if="product?.identity && description"
            v-model="description"
            :entityId="product.identity"
            :entityType="EntityType.PRODUCT"
            :isEditing="isEditing"
            :stekzModel="product"
            ref="textAppArtefact"
        />
    </section>

    <section :class="{ 'edit': isEditing }">
        <Processes :isEditing="isEditing" :processes="processes" />
    </section>

    <section :class="{ 'edit': isEditing }">
        <Documents
            v-if="product"
            entityType="product"
            :entity="product"
            :isEditing="isEditing"
            :artefacts="artefacts"
        />
    </section>
</template>

<script setup lang="ts">
import { useArtefactsManager } from "@/lib/artefacts/user";
import Documents from "@/components/Documents.vue";
import Spinner from "@/shared/Spinner.vue";
import TextAppArtefact from "@/components/TextAppArtefact.vue";
import Processes from "@/components/Processes/ProcessesList.vue";
import EditableTitle from "@/components/EditableTitle.vue";
import { ref, watch } from "vue";
import { useTaskManager } from "@/stores/task-store";
import { useProductManager } from "@/composables/product-manager";
import { useProcessesManager } from "@/stores/processes-store";
import { useEditManager } from "@/composables/edit-manager";
import { EntityType } from "@/stores/entity-store";
import { useGenerateDescriptionDialog } from "@/composables/generate-description";

const { artefacts } = useArtefactsManager();
useTaskManager();
const { isEditing } = useEditManager();
const { processes } = useProcessesManager();
const { product, isFetching, description } = useProductManager();
const { generateContent } = useGenerateDescriptionDialog();

const displaySpinner = ref(false);

watch(isFetching, (newValue) => (displaySpinner.value = newValue), {
    immediate: true,
});

const generateProductDescription = async () => {
    if (!product.value) {
        return;
    }
    await generateContent(
        product.value.identity,
        EntityType.PRODUCT,
        description.value?.updateContent.bind(description.value)
    );
};
</script>

<style scoped lang="scss">
.controls {
    text-wrap-mode: nowrap;
}

section {
    border: 1px solid transparent;
    padding: 1rem;

    &.description {
        padding-top: 0;
        z-index: 3;
    }

    &.edit {
        border-color: var(--stekz-outline);
        border-radius: 0.5rem;

        &.description {
            padding: 0;
        }
    }
}
</style>
